import { useMemo } from 'react'
import { ScoopText, ScoopTooltip } from '../../../../ui'
import { severityDistribution } from '../../utils/severityDistribution'
import { CANONICAL_SEVERITIES } from '../../utils/severity'
import styles from './SeverityDistributionBar.module.scss'

const SEGMENT_LABELS = {
    severe: 'Severe',
    moderate: 'Moderate',
    mild: 'Mild',
    healthy: 'Healthy',
    unprobed: 'Not probed',
}

/**
 * SeverityDistributionBar — the one-glance answer to "how bad is this run?"
 * A stacked proportion bar across the whole scanned scope (severe → healthy,
 * plus a grey never-probed segment), with a colored count legend beneath.
 * Colors come from the canonical severity module; the legend words carry the
 * meaning, so color never rides alone.
 */
export const SeverityDistributionBar = ({ entities = [], entitiesNoun = 'entities', className }) => {
    const counts = useMemo(() => severityDistribution(entities), [entities])
    if (counts.total === 0) return null

    const segments = [...CANONICAL_SEVERITIES, 'unprobed']
        .map((key) => ({ key, count: counts[key], label: SEGMENT_LABELS[key] }))
        .filter((segment) => segment.count > 0)

    return (
        <div className={`${styles.container} ${className || ''}`}>
            <div className={styles.bar} role="img" aria-label={`Severity distribution across ${counts.total} ${entitiesNoun}`}>
                {segments.map((segment) => (
                    <ScoopTooltip
                        key={segment.key}
                        title={`${segment.label}: ${segment.count} of ${counts.total}`}
                    >
                        <div
                            className={`${styles.segment} ${styles[`segment-${segment.key}`]}`}
                            // Portal spec (2026-07-28 convergence): 7% floor so
                            // a "1 severe in 47" sliver stays visible, count
                            // inside the segment.
                            style={{
                                width: `${Math.max((segment.count / counts.total) * 100, segment.count > 0 ? 7 : 0)}%`,
                            }}
                        >
                            <span className={styles.segmentCount}>{segment.count}</span>
                        </div>
                    </ScoopTooltip>
                ))}
            </div>
            <div className={styles.legend}>
                {segments.map((segment, index) => (
                    <ScoopText key={segment.key} className={styles.legendItem}>
                        {index > 0 && <span className={styles.legendSeparator}>·</span>}
                        <span className={`${styles.legendCount} ${styles[`legend-${segment.key}`]}`}>
                            {segment.count}
                        </span>{' '}
                        {segment.label}
                    </ScoopText>
                ))}
            </div>
        </div>
    )
}
